import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAssetsBaseUrl } from '../../../config/assetsUrl';

// 13 prvi, 12 treći; ostalo (1,3,4,5,6,7,8,9,10,11) izmiješano između
const galleryVideos = [
    '13.mp4',
    '12.mp4',
    '9.mp4',
    '3.mp4',
    '7.mp4',
    '4.mp4',
    '11.mp4',
    '8.mp4',
    '6.mp4',
    '10.mp4',
    '14.mp4',
    '15.mp4',
    '5.mp4',
    '1.mp4',
];

const VideoGallery: React.FC = () => {
    const { t } = useTranslation();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const handleClosePlayer = () => setActiveIndex(null);
    const handlePrev = () => {
        if (activeIndex === null) return;
        setActiveIndex((activeIndex - 1 + galleryVideos.length) % galleryVideos.length);
    };
    const handleNext = () => {
        if (activeIndex === null) return;
        setActiveIndex((activeIndex + 1) % galleryVideos.length);
    };

    useEffect(() => {
        if (activeIndex === null) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') handlePrev();
            if (event.key === 'ArrowRight') handleNext();
            if (event.key === 'Escape') handleClosePlayer();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeIndex]);

    if (galleryVideos.length === 0) {
        return (
            <Box py={4} textAlign="center">
                <Typography color="text2.primary">
                    {t('gallery.noVideos', 'Nema dostupnih videozapisa.')}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                    },
                    gap: { xs: 2, sm: 3 },
                }}
            >
                {galleryVideos.map((filename, currentIndex) => {
                    const src = `${getAssetsBaseUrl()}/videos/${encodeURIComponent(filename)}`;
                    const posterFilename = filename.replace(/\.mp4$/i, '.jpg');
                    const posterSrc = `${getAssetsBaseUrl()}/videos/${encodeURIComponent(posterFilename)}`;
                    return (
                        <Box
                            key={filename}
                            onClick={() => setActiveIndex(currentIndex)}
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 2,
                                backgroundColor: 'rgba(0,0,0,0.08)',
                                aspectRatio: '16 / 9',
                                cursor: 'pointer',
                                '& video': {
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    background: 'rgba(0,0,0,0.2)',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease',
                                },
                                '&:hover::after': { opacity: 1 },
                            }}
                        >
                            <video
                                src={src}
                                poster={posterSrc}
                                playsInline
                                preload="metadata"
                                title={filename}
                                muted
                                loop
                                onMouseEnter={(e) => e.currentTarget.play()}
                                onMouseLeave={(e) => {
                                    e.currentTarget.pause();
                                    e.currentTarget.currentTime = 0;
                                }}
                            />
                        </Box>
                    );
                })}
            </Box>

            <Dialog
                open={activeIndex !== null}
                onClose={handleClosePlayer}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.95)',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                }}
            >
                {activeIndex !== null && (
                    <>
                        <IconButton
                            onClick={handleClosePlayer}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <IconButton
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute',
                                left: { xs: 8, md: 20 },
                                zIndex: 1,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                p: 2,
                            }}
                        >
                            <video
                                key={galleryVideos[activeIndex]}
                                src={`${getAssetsBaseUrl()}/videos/${encodeURIComponent(galleryVideos[activeIndex])}`}
                                controls
                                playsInline
                                autoPlay
                                style={{
                                    width: 'auto',
                                    height: 'auto',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        </Box>
                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: { xs: 8, md: 20 },
                                zIndex: 1,
                                color: '#fff',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default VideoGallery;
